/**
 * Modelo do documento de proposta e do cadastro de pessoas.
 *
 * O documento em JSON é a **fonte de verdade** (decisão D1). O HTML publicado é artefato derivado.
 * Por isso este schema precisa guardar tudo que é necessário para:
 *
 *   1. recalcular o preço no servidor e detectar divergência com o cliente (decisão D6);
 *   2. reconstruir a proposta exatamente como foi emitida, meses depois;
 *   3. auditar quem alterou o que e quando.
 *
 * O legado descartava margem, custo líquido, PTAX, custo de deploy, licenças, duração do contrato e
 * janela do crédito. O inventário confirmou que **não existe tabela alguma** do construtor na conta,
 * ou seja, nenhuma proposta emitida até hoje é auditável. Aqui isso deixa de ser possível.
 */

import { z } from 'zod';

// ---------------------------------------------------------------------------
// Primitivos
// ---------------------------------------------------------------------------

/**
 * Versão corrente do schema. Incrementar exige acrescentar uma migração em `migrarDocumento`.
 *
 * v1 → v2: acrescentou `oportunidade`, `sincronizacao` e `formalizacao`, para vincular a proposta à
 * oportunidade do Dynamics e suportar a variante formal assinável (decisões D13 e D14).
 */
export const VERSAO_SCHEMA_ATUAL = 2;

const textoCurto = z.string().trim().min(1).max(200);
const textoMedio = z.string().trim().min(1).max(600);
const textoLongo = z.string().trim().min(1).max(5000);

/**
 * Data no formato `AAAA-MM-DD`, sem hora, para evitar ambiguidade de fuso na validade.
 *
 * A checagem de calendário é feita comparando os componentes, e não por `Date.parse`: o parser do
 * JavaScript é permissivo e aceita formatos não-ISO, o que deixaria passar entrada inválida.
 */
export const dataSimples = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use o formato AAAA-MM-DD.')
  .refine((v) => {
    const [ano, mes, dia] = v.split('-').map(Number) as [number, number, number];
    if (mes < 1 || mes > 12 || dia < 1) return false;
    const d = new Date(Date.UTC(ano, mes - 1, dia));
    return d.getUTCFullYear() === ano && d.getUTCMonth() === mes - 1 && d.getUTCDate() === dia;
  }, 'Data inexistente no calendário.');

/**
 * Instante em ISO 8601 com fuso explícito, usado nos metadados de auditoria.
 *
 * O formato é exigido por expressão regular antes de qualquer tentativa de parse, porque
 * `Date.parse('04/08/2026 14:00')` é aceito pelo V8 e produziria um instante silenciosamente errado.
 */
export const instante = z
  .string()
  .regex(
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?(Z|[+-]\d{2}:\d{2})$/,
    'Use ISO 8601 com fuso explícito, por exemplo 2026-08-04T14:00:00.000Z.',
  )
  .refine((v) => !Number.isNaN(Date.parse(v)), 'Instante inexistente no calendário.');

/**
 * Identificador do usuário no Cognito. É o `sub`, não o e-mail.
 *
 * Valida apenas a **forma** 8-4-4-4-12, deliberadamente sem exigir conformidade com a RFC 4122.
 * Os `sub` reais deste pool não são UUID válidos pela norma: `a14bd500-80a1-70f5-0a7d-edac27c2faba`
 * tem nibble de versão 7 e nibble de variante `0`, que a RFC não admite. Usar um validador estrito
 * de UUID recusaria todos os 11 usuários existentes.
 */
export const sub = z
  .string()
  .regex(
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
    'O identificador do usuário deve ser o sub do Cognito, no formato 8-4-4-4-12.',
  );

/**
 * E-mail corporativo. A restrição de domínio existe no servidor via trigger `verify-emaster-email`,
 * e é repetida aqui para dar erro imediato no formulário.
 */
export const emailCorporativo = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'E-mail inválido.')
  .refine((v) => v.endsWith('@emaster.info'), 'O e-mail deve ser do domínio @emaster.info.');

/**
 * WhatsApp com 13 dígitos, sem `+`, sem espaço e sem hífen — o formato aceito por `wa.me`.
 * Dois registros do `sellers.json` legado estão quebrados por omitir o nono dígito.
 */
export const whatsapp = z
  .string()
  .trim()
  .regex(/^55\d{2}9\d{8}$/, 'Use 13 dígitos: 55 + DDD + 9 + 8 dígitos. Exemplo: 5531990745722.');

/** Telefone de exibição no padrão da marca. */
export const telefoneExibicao = z
  .string()
  .trim()
  .regex(/^\+55 \d{2} 9\d{4}-\d{4}$/, 'Use o formato +55 DD 9XXXX-XXXX.');

/** Número da proposta no padrão `EM-AAAA-NNN`, ausente em todas as 11 propostas do acervo. */
export const numeroProposta = z
  .string()
  .regex(/^EM-\d{4}-\d{3,}$/, 'Use o formato EM-AAAA-NNN, por exemplo EM-2026-001.');

const urlHttps = z
  .url('URL inválida.')
  .refine((v) => v.startsWith('https://'), 'Todo asset deve usar HTTPS.');

/**
 * GUID do Dataverse.
 *
 * Diferente de `sub`, aqui a forma canônica com hífens é garantida pela plataforma da Microsoft, e
 * o valor é sempre gerado pelo Dataverse — nunca digitado. Ainda assim validamos só a forma, porque
 * a versão do UUID não é contrato documentado.
 */
export const guidDataverse = z
  .string()
  .regex(
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
    'O identificador do Dataverse deve ser um GUID no formato 8-4-4-4-12.',
  );

/**
 * Número da oportunidade como o vendedor o conhece no Dynamics.
 *
 * Formato observado no acervo: `O24138065` — a letra `O` seguida de dígitos. A validação aceita de
 * 4 a 20 caracteres alfanuméricos para não recusar numeração histórica ou de outra unidade, mas
 * exige ao menos um dígito, o que barra o caso de alguém digitar o nome da oportunidade aqui.
 */
export const numeroOportunidade = z
  .string()
  .trim()
  .toUpperCase()
  .regex(
    /^(?=.*\d)[A-Z0-9-]{4,20}$/,
    'Informe o número da oportunidade do Dynamics, por exemplo O24138065.',
  );

// ---------------------------------------------------------------------------
// Oportunidade do CRM
// ---------------------------------------------------------------------------

/**
 * Vínculo com a oportunidade do Dynamics 365.
 *
 * O vínculo é opcional na publicação da proposta. Quando o vendedor escolhe uma oportunidade, os
 * dados são congelados para manter a rastreabilidade mesmo que o registro seja renomeado no CRM.
 * Uma operação específica que crie Cotação vinculada pode exigir a GUID por meio de
 * `exigirOportunidade`; a publicação da proposta, por si só, aceita `null`.
 *
 * Os campos importados são **congelados**, pela mesma razão que o responsável é: a proposta precisa
 * refletir o que era verdade na emissão, e o cliente pode ser renomeado no CRM depois.
 */
export const oportunidadeCrm = z.object({
  /** Número visível ao vendedor. É o que ele digita quando o Construtor é aberto fora do CRM. */
  numero: numeroOportunidade,
  /**
   * GUID da oportunidade no Dataverse. Nulo quando a proposta foi iniciada pelo número digitado e a
   * resolução para GUID ainda não aconteceu. A publicação exige que já esteja resolvido.
   */
  id: guidDataverse.nullable(),
  /** Nome da oportunidade, como aparece no CRM. Vira o nome da Cotação. */
  nome: textoCurto.nullable(),
  /** Conta (cliente) vinculada à oportunidade. */
  contaId: guidDataverse.nullable(),
  contaNome: textoCurto.nullable(),
  /** Moeda da transação no CRM. Precisa concordar com a trilha da proposta. */
  moeda: z.enum(['USD', 'BRL']).nullable(),
  /** Lista de preços do CRM, quando o ambiente exigir. */
  listaPrecoId: guidDataverse.nullable(),
  /** Dono da oportunidade no CRM. Pode diferir de quem monta a proposta. */
  proprietarioEmail: z.string().trim().toLowerCase().nullable(),
  /** Quando os dados acima foram importados do CRM. Nulo quando foram digitados à mão. */
  importadoEm: instante.nullable(),
});
export type OportunidadeCrm = z.infer<typeof oportunidadeCrm>;

/** Situação da sincronização com o CRM, espelhando o `IntegrationStatus` da Cotação. */
export const situacaoSincronizacao = z.enum([
  'nao_sincronizada',
  'aguardando',
  'sincronizada',
  'erro_validacao',
  'erro_tecnico',
]);
export type SituacaoSincronizacao = z.infer<typeof situacaoSincronizacao>;

/**
 * Resultado da sincronização direta com o Dataverse, gravado pela Lambda.
 *
 * Fica no documento, e não em tabela separada, porque é informação da proposta: quem abre a versão 3
 * precisa saber qual Cotação ela gerou. O log técnico detalhado permanece no CloudWatch/Dataverse.
 */
export const sincronizacaoCrm = z.object({
  situacao: situacaoSincronizacao,
  /** GUID da Cotação criada no Dynamics. */
  cotacaoId: guidDataverse.nullable(),
  /** Número legível da Cotação, por exemplo `QUO-04480-L6Z9Q2`. Vai no topo da proposta formal. */
  cotacaoNumero: textoCurto.nullable(),
  /** Identificador de correlação do Dataverse, quando disponibilizado pelo adaptador. */
  correlacaoId: textoCurto.nullable(),
  sincronizadoEm: instante.nullable(),
  /** Mensagem do último erro, quando a situação é de erro. */
  erro: textoMedio.nullable(),
});
export type SincronizacaoCrm = z.infer<typeof sincronizacaoCrm>;

// ---------------------------------------------------------------------------
// Formalização: contratos, condições e aceite
// ---------------------------------------------------------------------------

/**
 * Contrato registrado em cartório ao qual o cliente adere ao assinar a proposta.
 *
 * O modelo da Anestech lista dois, com número de registro e cartório. São eles que dão à proposta
 * formal o caráter de termo de adesão em vez de simples orçamento.
 */
export const contratoRegistrado = z.object({
  titulo: textoMedio,
  registro: z
    .string()
    .trim()
    .regex(/^\d{4,10}$/, 'O número de registro do contrato deve ter de 4 a 10 dígitos.'),
});
export type ContratoRegistrado = z.infer<typeof contratoRegistrado>;

/**
 * Condições comerciais da proposta formal.
 *
 * Deliberadamente **não** são texto livre: cada item viraria uma frase divergente entre propostas, e
 * é justamente o tipo de cláusula que precisa ser idêntica em todas. O renderizador monta as frases
 * a partir destes campos mais a duração do contrato e a moeda, que já estão nas premissas.
 */
export const condicoesFormalizacao = z.object({
  formaPagamento: textoCurto,
  /** Dia limite de emissão da fatura. No modelo: 5. */
  diaFaturamento: z.number().int().min(1).max(28),
  /** Dia de vencimento, ou próximo dia útil. No modelo: 10. */
  diaVencimento: z.number().int().min(1).max(28),
  impostosInclusos: z.boolean(),
  /**
   * Percentual da estimativa mensal usado para calcular o projeto de implantação. No modelo: 100.
   * Nulo quando não há implantação cobrada.
   */
  percentualProjetoImplantacao: z.number().min(0).max(200).nullable(),
  /** Cláusulas adicionais específicas desta proposta. Entram após as fixas. */
  clausulasAdicionais: z.array(textoMedio),
});
export type CondicoesFormalizacao = z.infer<typeof condicoesFormalizacao>;

/** Como a assinatura do cliente é coletada. */
export const meioAssinatura = z.enum(['manual', 'clique_eletronico', 'provedor_externo']);
export type MeioAssinatura = z.infer<typeof meioAssinatura>;

/**
 * Registro do aceite do cliente.
 *
 * A evidência do aceite é o par (hash do documento assinado, instante), não o PDF em si: o PDF é
 * artefato derivado, e guardar o hash permite provar que o arquivo arquivado é o mesmo que foi
 * aceito, mesmo que ele seja movido entre SharePoint e Dataverse.
 */
export const aceiteCliente = z.object({
  meio: meioAssinatura,
  /** Nome do responsável que assinou. Nulo enquanto não houver aceite. */
  assinadoPor: textoCurto.nullable(),
  /** Documento de identificação informado no aceite, como no bloco do modelo. */
  documentoIdentificacao: textoCurto.nullable(),
  assinadoEm: instante.nullable(),
  /** SHA-256 em hexadecimal do PDF exatamente como assinado. */
  hashDocumento: z
    .string()
    .regex(/^[0-9a-f]{64}$/i, 'O hash deve ser um SHA-256 em hexadecimal.')
    .nullable(),
  /** Endereço de origem do aceite, quando por clique. Evidência de auditoria. */
  ipOrigem: z.string().trim().max(45).nullable(),
  /** URL do documento assinado arquivado, no SharePoint ou no Dataverse. */
  documentoAssinadoUrl: urlHttps.nullable(),
});
export type AceiteCliente = z.infer<typeof aceiteCliente>;

/**
 * Bloco de formalização da proposta.
 *
 * Só é exigido quando a proposta vai ser emitida na variante formal. Uma proposta de apresentação,
 * usada para negociação, não precisa de termo de adesão.
 */
export const formalizacao = z.object({
  contratos: z.array(contratoRegistrado).min(1),
  /** Cartório onde os contratos estão registrados. */
  cartorio: textoMedio,
  /** Razão social e dados de cobrança do cliente, distintos do nome comercial. */
  clienteCobranca: z.object({
    razaoSocial: textoCurto,
    cnpj: z
      .string()
      .trim()
      .regex(/^\d{14}$/, 'Informe o CNPJ com 14 dígitos, sem pontuação.')
      .nullable(),
    endereco: textoMedio.nullable(),
    cidade: textoCurto.nullable(),
    uf: z
      .string()
      .trim()
      .toUpperCase()
      .regex(/^[A-Z]{2}$/, 'Use a sigla de duas letras da UF.')
      .nullable(),
    cep: z
      .string()
      .trim()
      .regex(/^\d{8}$/, 'Informe o CEP com 8 dígitos, sem pontuação.')
      .nullable(),
  }),
  condicoes: condicoesFormalizacao,
  aceite: aceiteCliente,
});
export type Formalizacao = z.infer<typeof formalizacao>;

// ---------------------------------------------------------------------------
// Cadastro de pessoas
// ---------------------------------------------------------------------------

/**
 * Perfil de acesso. Enum fechado, distinto do cargo de exibição (decisão D5).
 *
 * A autorização vem do **grupo do Cognito**, não deste campo nem do `custom:role` — o atributo é
 * gravável pelo próprio usuário (risco aceito R2), então não serve como fonte de verdade.
 *
 * **`inside_sales` não existe como perfil**, por decisão do usuário: inside sales e vendedor são a
 * mesma função na eMaster, e manter dois perfis idênticos só criaria a dúvida de qual atribuir. O
 * valor legado `Inside Sales` do `custom:role` é traduzido para `vendedor` por
 * `traduzirPapelLegado`, e `papelLegadoDoPerfil` continua gravando `Inside Sales` de volta, para não
 * quebrar o painel legado que compartilha este pool.
 */
export const perfilAcesso = z.enum(['admin', 'diretor', 'vendedor', 'leitor']);
export type PerfilAcesso = z.infer<typeof perfilAcesso>;

export const situacaoPessoa = z.enum(['ativa', 'desabilitada', 'aguardando_liberacao']);
export type SituacaoPessoa = z.infer<typeof situacaoPessoa>;

/**
 * Registro de pessoa, substituto do `js/sellers.json` estático.
 *
 * O legado casava vendedor por e-mail contra um arquivo com 5 registros, enquanto o pool tem 11
 * usuários. Vendedor ausente do arquivo resultava em campo de responsável vazio, sem aviso.
 */
export const pessoa = z.object({
  sub,
  email: emailCorporativo,
  nomeExibicao: textoCurto,
  /** Cargo como deve aparecer no card de contato da proposta. Texto livre, não é permissão. */
  cargoExibicao: textoCurto,
  telefone: telefoneExibicao,
  whatsapp,
  fotoUrl: urlHttps.nullable(),
  perfilAcesso,
  situacao: situacaoPessoa,
});
export type Pessoa = z.infer<typeof pessoa>;

/**
 * Dados do responsável **congelados** no documento publicado.
 *
 * Um vendedor que troque de cargo ou saia da empresa não deve alterar retroativamente uma proposta
 * já enviada ao cliente. Por isso o documento guarda uma cópia, não uma referência viva.
 */
export const responsavelCongelado = z.object({
  sub,
  email: emailCorporativo,
  nomeExibicao: textoCurto,
  cargoExibicao: textoCurto,
  telefone: telefoneExibicao,
  whatsapp,
  fotoUrl: urlHttps.nullable(),
});
export type ResponsavelCongelado = z.infer<typeof responsavelCongelado>;

// ---------------------------------------------------------------------------
// Premissas de cálculo
// ---------------------------------------------------------------------------

export const trilha = z.enum(['CS', 'PS']);
export const planoSuporte = z.enum(['nenhum', 'developer', 'business', 'enterprise']);
export const duracaoContratoMeses = z.union([z.literal(12), z.literal(36)]);
export const janelaCreditoMeses = z.union([
  z.literal(3),
  z.literal(4),
  z.literal(5),
  z.literal(6),
  z.literal(12),
]);
export const modalidadeDeploy = z.enum(['nao_incluido', 'faturado', 'financiado', 'cortesia']);

/** Item do CSV da calculadora, com o custo líquido preservado para auditoria. */
export const itemCusto = z.object({
  servico: textoCurto,
  descricao: textoCurto,
  /**
   * Memória de cálculo do item. Aceita vazio, e o mínimo de 1 caractere seria errado aqui.
   *
   * Recurso lançado na calculadora sem nada em `Configuration summary` simplesmente não tem memória
   * a declarar — transferência de dados deixada sem volume é o caso comum. Exigir texto obrigava o
   * parser a inventar um, e foi o que travou a prévia com `itensCusto.1.resumoConfiguracao: Invalid
   * input` depois que o item de custo zero passou a ser preservado.
   *
   * Os três consumidores já esperavam a possibilidade: `formal.ts` e `crm.ts` filtram o texto vazio
   * ao montar o detalhe, e a tabela de investimento emite a célula em branco.
   */
  resumoConfiguracao: z.string().trim().max(600),
  grupo: textoCurto,
  custoMensalLiquidoUsd: z.number().nonnegative(),
});

export const licencasBackup = z.object({
  fileServer: z.number().int().nonnegative().default(0),
  virtualServer: z.number().int().nonnegative().default(0),
  sharepoint: z.number().int().nonnegative().default(0),
  vmwareHyperv: z.number().int().nonnegative().default(0),
});

export const planoSafeStorage = z.enum(['orbita', 'constelacao', 'singularidade', 'enterprise']);

export const focoAssessment = z.enum(['custos', 'seguranca']);

/**
 * Custo adicional nomeado pelo vendedor, congelado na proposta.
 *
 * A descrição é obrigatória e não pode ser vazia: um item de preço sem nome é linha que o cliente lê
 * como "custo não identificado", e é exatamente o tipo de item que trava aprovação de compra. O valor
 * está na **moeda de entrada da trilha** — USD em CS, BRL em PS —, o que é registrado aqui porque o
 * documento precisa ser reconstruível sem consultar a trilha em outro lugar.
 */
export const itemCustoAdicional = z.object({
  descricao: textoCurto,
  valorMensal: z.number().nonnegative(),
});

/**
 * Assinatura do eMaster SafeStorage congelada na proposta.
 *
 * A contagem de usuários fica registrada porque é ela que justifica a faixa cobrada: sem o número, um
 * reajuste por crescimento de equipe não tem base contra o que comparar.
 */
export const safestorage = z.object({
  plano: planoSafeStorage,
  usuarios: z.number().int().positive(),
  /** Mensalidade negociada em USD. Obrigatória na faixa Enterprise, que é sob consulta. */
  mensalidadeCustomizadaUsd: z.number().nonnegative().nullable(),
});

/**
 * Tudo que é necessário para recalcular o preço.
 *
 * Se um campo daqui faltar, a proposta não é reconstruível — e foi exatamente o que aconteceu com
 * todo o histórico do legado.
 */
export const premissas = z.object({
  trilha,
  planoSuporte,
  duracaoContratoMeses,

  /** Cotação usada no momento da emissão. Congelada: não reconsultar ao reabrir a proposta. */
  ptax: z.number().positive().nullable(),
  /** Origem da cotação, para rastreabilidade. */
  ptaxOrigem: z.enum(['api', 'manual', 'nao_aplicavel']),

  itensCusto: z.array(itemCusto).nullable(),
  /** Região AWS lida do CSV. O design system exige declará-la na seção de investimento. */
  regiaoAws: textoCurto.nullable(),
  /** Consumo digitado quando não houve CSV. Na moeda de entrada da trilha. */
  consumoManual: z.number().nonnegative().nullable(),

  credito: z
    .object({
      valorUsd: z.number().nonnegative(),
      janelaMeses: janelaCreditoMeses,
    })
    .nullable(),

  deploy: z.object({
    modalidade: modalidadeDeploy,
    custo: z.number().nonnegative(),
  }),

  licencasBackup: licencasBackup.nullable(),

  /** Assinatura do SafeStorage. `null` quando o produto não faz parte da proposta. */
  safestorage: safestorage.nullable().default(null),

  /**
   * Focos do assessment oferecido como cortesia. `null` quando não há assessment na proposta.
   *
   * Substitui o antigo `incluiAssessment`, que era booleano e não dizia **quais** avaliações estavam
   * sendo prometidas — informação sem a qual a proposta não podia descrever o que o cliente ia
   * receber. O campo antigo é descartado na leitura, porque o schema não é `strict`: proposta do
   * acervo com `incluiAssessment: true` passa a valer como sem assessment, que é exatamente o que
   * ela já mostrava ao cliente, já que nenhum renderizador consumia o sinalizador.
   *
   * O `preprocess` aceita um valor solto além da lista. Entre a introdução do campo e a inclusão da
   * opção "ambos" o formato foi `'custos'` em vez de `['custos']`, e documento é registro de longa
   * duração (decisão D1): recusar o formato anterior na releitura tornaria irrecuperável a proposta
   * emitida nessa janela.
   */
  assessment: z
    .preprocess(
      (v) => (typeof v === 'string' ? [v] : v),
      z
        .array(focoAssessment)
        .min(1, 'Com assessment na proposta, informe ao menos um foco.')
        .refine((focos) => new Set(focos).size === focos.length, {
          message: 'O mesmo foco de assessment aparece duas vezes.',
        })
        .nullable(),
    )
    .default(null),

  sessoesTreinamento: z.number().int().min(0).max(3),

  /**
   * Custos adicionais nomeados pelo vendedor. `null` quando não há nenhum.
   *
   * Lista, e não um único item, porque o nome do produto é plural e porque concatenar dois serviços
   * numa descrição só destrói a itemização que a tabela de investimento precisa mostrar. Vendedor que
   * precisa de dois itens e tem um campo acaba escrevendo "Módulo 24x7 + PAM" numa linha, e aí o
   * cliente não sabe o preço de nenhum dos dois.
   *
   * `default(null)` pelo mesmo motivo de `safestorage` e `assessment`: proposta emitida antes do campo
   * existir não o traz, e sem o default o schema recusaria todo o acervo já publicado na releitura.
   */
  custosAdicionais: z
    .array(itemCustoAdicional)
    .min(1, 'Com custos adicionais na proposta, informe ao menos um item.')
    .nullable()
    .default(null),
});
export type Premissas = z.infer<typeof premissas>;

// ---------------------------------------------------------------------------
// Resultados calculados
// ---------------------------------------------------------------------------

const valor = z.object({
  usd: z.number(),
  brl: z.number().nullable(),
});

/**
 * Cópia dos valores calculados no momento da emissão.
 *
 * Guardados para auditoria e para o servidor comparar com o próprio recálculo (decisão D6).
 * Não são fonte de verdade: em caso de divergência, vence o recálculo a partir das premissas.
 */
export const resultados = z.object({
  margemPercentual: z.number().min(0).max(100),
  margemAplicada: z.boolean(),
  custoLiquidoTotal: valor.nullable(),
  consumo: valor,
  custoSuporte: valor,
  baseMensal: valor,
  licencasBackup: valor,
  /**
   * Assinatura mensal da plataforma SafeStorage.
   *
   * Tem `default` porque propostas emitidas antes do produto existir não trazem o campo, e sem ele
   * o schema recusaria todo o acervo já publicado na primeira releitura.
   */
  plataformaSafeStorage: valor.default({ usd: 0, brl: null }),
  /**
   * Soma dos custos adicionais. Tem `default` pela mesma razão de `plataformaSafeStorage`: o acervo
   * publicado antes do campo existir não o traz, e recusá-lo tornaria as propostas antigas ilegíveis.
   */
  custosAdicionais: valor.default({ usd: 0, brl: null }),
  deployCustoTotal: valor,
  deployFaturado: valor,
  creditoAplicadoNoDeploy: valor,
  creditoResidual: valor,
  reducaoMensal: valor,
  mensalidadeEstimada: valor,
  economiaPercentual: z.number(),
  exibirEconomia: z.boolean(),
});
export type Resultados = z.infer<typeof resultados>;

// ---------------------------------------------------------------------------
// Conteúdo editorial
// ---------------------------------------------------------------------------

/** Seção de texto livre da proposta, na anatomia do design system. */
export const secaoConteudo = z.object({
  /** Âncora da seção. Precisa ser um slug para virar `id` no HTML. */
  ancora: z
    .string()
    .regex(/^[a-z0-9-]+$/, 'A âncora deve conter apenas minúsculas, números e hífen.'),
  eyebrow: textoCurto,
  titulo: textoCurto,
  paragrafos: z.array(textoLongo).min(1),
});

export const itemLista = z.object({
  texto: textoMedio,
  destaque: textoCurto.nullable(),
});

export const passoCronograma = z.object({
  titulo: textoCurto,
  descricao: textoMedio,
  duracao: textoCurto.nullable(),
});

export const conteudo = z.object({
  /** Badge de contexto no herói, acima do H1. */
  badgeContexto: textoCurto,
  /** Complemento do H1. A fórmula `Para a [CLIENTE]: ...` é montada pelo renderizador. */
  promessaValor: textoMedio,
  subtituloHero: textoLongo,

  secoes: z.array(secaoConteudo),
  escopoIncluido: z.array(itemLista),
  escopoExcluido: z.array(itemLista),
  proximosPassos: z.array(passoCronograma),
  condicoesComerciais: z.array(
    z.object({ titulo: textoCurto, descricao: textoMedio, icone: textoCurto }),
  ),

  /** Memória de cálculo e observações, vindas do editor rich-text. HTML já sanitizado. */
  observacoesHtml: z.string().max(50_000).nullable(),
});
export type Conteudo = z.infer<typeof conteudo>;

// ---------------------------------------------------------------------------
// Auditoria e situação
// ---------------------------------------------------------------------------

export const situacaoProposta = z.enum(['rascunho', 'publicada', 'substituida']);
export type SituacaoProposta = z.infer<typeof situacaoProposta>;

export const auditoria = z.object({
  /** Número sequencial da versão do documento. A primeira publicação é 1. */
  versao: z.number().int().positive(),
  criadoPor: sub,
  criadoEm: instante,
  atualizadoPor: sub,
  atualizadoEm: instante,
  /** Motivo da alteração, obrigatório ao editar proposta já publicada. */
  motivoAlteracao: textoMedio.nullable(),
});
export type Auditoria = z.infer<typeof auditoria>;

// ---------------------------------------------------------------------------
// Documento
// ---------------------------------------------------------------------------

export const documentoProposta = z
  .object({
    versaoSchema: z.literal(VERSAO_SCHEMA_ATUAL),
    /** Versão da tabela de constantes usada no cálculo, para reconstrução fiel. */
    versaoConfiguracao: textoCurto,
    /** Versão do template de renderização, para saber com qual layout foi emitida. */
    versaoTemplate: textoCurto,

    numero: numeroProposta,
    situacao: situacaoProposta,

    cliente: textoCurto,
    projeto: textoCurto,

    emitidaEm: dataSimples,
    validaAte: dataSimples,

    /**
     * Vínculo opcional com uma oportunidade do CRM.
     *
     * `null` significa que a proposta foi publicada sem oportunidade associada. Quando preenchido,
     * o bloco conserva o número e a GUID usados para rastreabilidade e para integrações específicas.
     */
    oportunidade: oportunidadeCrm.nullable(),
    /** Estado da sincronização com a Cotação do Dynamics. */
    sincronizacao: sincronizacaoCrm,
    /**
     * Dados de formalização. Nulo quando a proposta é só de apresentação; obrigatório para emitir a
     * variante formal, que é a que o cliente assina.
     */
    formalizacao: formalizacao.nullable(),

    responsavel: responsavelCongelado,
    premissas,
    resultados,
    conteudo,
    auditoria,
  })
  .refine((d) => d.validaAte >= d.emitidaEm, {
    message: 'A data de validade não pode ser anterior à data de emissão.',
    path: ['validaAte'],
  })
  .refine((d) => d.premissas.trilha !== 'PS' || d.premissas.ptax !== null, {
    message: 'A trilha PS exige a cotação do dólar (PTAX).',
    path: ['premissas', 'ptax'],
  })
  .refine(
    (d) => {
      const temCsv = d.premissas.itensCusto !== null && d.premissas.itensCusto.length > 0;
      return temCsv || d.premissas.consumoManual !== null;
    },
    {
      message: 'Sem itens do CSV, é obrigatório informar o consumo manualmente.',
      path: ['premissas', 'consumoManual'],
    },
  )
  .refine(
    (d) => {
      const temLicencas =
        d.premissas.licencasBackup !== null &&
        Object.values(d.premissas.licencasBackup).some((q) => q > 0);
      return !temLicencas || d.premissas.ptax !== null;
    },
    {
      message: 'As licenças de backup são cotadas em BRL e exigem a cotação do dólar (PTAX).',
      path: ['premissas', 'ptax'],
    },
  )
  .refine(
    (d) => {
      // A faixa Enterprise é sob consulta: sem a mensalidade negociada, a proposta sairia cobrando
      // zero pela plataforma. O motor recusa, e o schema recusa antes de gravar.
      //
      // Basta comparar com nulo: o campo tem `default(null)`, então o tipo de saída do schema nunca
      // é `undefined` — a ausência da chave já virou nulo antes de chegar aqui.
      const s = d.premissas.safestorage;
      if (s === null) return true;
      return s.plano !== 'enterprise' || s.mensalidadeCustomizadaUsd !== null;
    },
    {
      message:
        'O plano SafeStorage Enterprise é sob consulta: informe a mensalidade negociada em dólar.',
      path: ['premissas', 'safestorage', 'mensalidadeCustomizadaUsd'],
    },
  )
  .refine(
    (d) => {
      // A moeda da oportunidade no CRM precisa concordar com a trilha: PS fatura em BRL, CS em USD.
      // Divergência aqui produziria uma Cotação com o valor certo na moeda errada, que é o pior
      // desfecho possível — passa pela conferência de total e chega errado ao financeiro.
      const moeda = d.oportunidade?.moeda;
      if (moeda === undefined || moeda === null) return true;
      return d.premissas.trilha === 'PS' ? moeda === 'BRL' : moeda === 'USD';
    },
    {
      message:
        'A moeda da oportunidade no CRM não concorda com a trilha: PS fatura em BRL e CS em USD.',
      path: ['oportunidade', 'moeda'],
    },
  )
  .refine(
    (d) => {
      // Aceite registrado sem hash do documento não é evidência de nada: não dá para provar qual
      // arquivo foi aceito. Se há assinatura, há hash.
      const a = d.formalizacao?.aceite;
      if (a === undefined) return true;
      if (a.assinadoEm === null) return true;
      return a.hashDocumento !== null && a.assinadoPor !== null;
    },
    {
      message: 'Um aceite registrado exige o nome do signatário e o hash do documento assinado.',
      path: ['formalizacao', 'aceite', 'hashDocumento'],
    },
  );

export type DocumentoProposta = z.infer<typeof documentoProposta>;

// ---------------------------------------------------------------------------
// Guardas de publicação
// ---------------------------------------------------------------------------

/**
 * Erro de regra de publicação, distinto de erro de schema.
 *
 * Existe para operações que têm pré-condições além do schema, como gerar uma Cotação vinculada no
 * Dynamics ou emitir a variante formal. A publicação comum aceita proposta sem oportunidade.
 */
export class ErroPublicacao extends Error {
  readonly codigo: string;

  constructor(codigo: string, mensagem: string) {
    super(mensagem);
    this.name = 'ErroPublicacao';
    this.codigo = codigo;
  }
}

/**
 * Oportunidade com o GUID já resolvido.
 *
 * Tipo distinto de `OportunidadeCrm` para que a garantia viaje no sistema de tipos: uma função que
 * recebe isto não precisa checar `id` de novo, nem usar cast.
 */
export type OportunidadeResolvida = Omit<OportunidadeCrm, 'id'> & { readonly id: string };

/**
 * Exige oportunidade resolvida antes de uma operação que precise criar vínculo no Dynamics.
 *
 * Esta guarda não faz parte da publicação comum da proposta. Ela deve ser usada somente quando a
 * operação seguinte realmente precisa de número e GUID, como a criação de uma Cotação vinculada.
 * Sem GUID, procurar por nome reintroduziria ambiguidade.
 */
export function exigirOportunidade(doc: DocumentoProposta): OportunidadeResolvida {
  const o = doc.oportunidade;

  if (o === null) {
    throw new ErroPublicacao(
      'OPORTUNIDADE_AUSENTE',
      'Informe o número da oportunidade do Dynamics antes de publicar.',
    );
  }
  if (o.id === null) {
    throw new ErroPublicacao(
      'OPORTUNIDADE_NAO_RESOLVIDA',
      `A oportunidade ${o.numero} ainda não foi localizada no Dynamics. Confirme o número.`,
    );
  }

  return { ...o, id: o.id };
}

/** Exige o bloco de formalização para emitir a variante que o cliente assina. */
export function exigirFormalizacao(doc: DocumentoProposta): Formalizacao {
  if (doc.formalizacao === null) {
    throw new ErroPublicacao(
      'FORMALIZACAO_AUSENTE',
      'A proposta comercial formal exige os contratos, as condições e os dados de cobrança.',
    );
  }
  return doc.formalizacao;
}

// ---------------------------------------------------------------------------
// Validação e migração
// ---------------------------------------------------------------------------

export interface ResultadoValidacao<T> {
  readonly valido: boolean;
  readonly dados: T | null;
  readonly problemas: readonly { readonly campo: string; readonly mensagem: string }[];
}

function formatarProblemas(erro: z.ZodError): { campo: string; mensagem: string }[] {
  return erro.issues.map((i) => ({
    campo: i.path.length > 0 ? i.path.join('.') : '(raiz)',
    mensagem: i.message,
  }));
}

/** Valida um documento e devolve problemas legíveis, em vez de lançar. */
export function validarDocumento(bruto: unknown): ResultadoValidacao<DocumentoProposta> {
  const r = documentoProposta.safeParse(bruto);
  return r.success
    ? { valido: true, dados: r.data, problemas: [] }
    : { valido: false, dados: null, problemas: formatarProblemas(r.error) };
}

/** Valida um registro de pessoa. */
export function validarPessoa(bruto: unknown): ResultadoValidacao<Pessoa> {
  const r = pessoa.safeParse(bruto);
  return r.success
    ? { valido: true, dados: r.data, problemas: [] }
    : { valido: false, dados: null, problemas: formatarProblemas(r.error) };
}

export class ErroMigracao extends Error {
  readonly versaoEncontrada: unknown;

  constructor(mensagem: string, versaoEncontrada: unknown) {
    super(mensagem);
    this.name = 'ErroMigracao';
    this.versaoEncontrada = versaoEncontrada;
  }
}

/**
 * Sincronização inicial, para documento que nunca foi ao CRM.
 *
 * Exportada porque tanto a migração quanto a criação de proposta nova precisam do mesmo ponto de
 * partida, e duplicar o literal levaria as duas a divergirem.
 */
export function sincronizacaoInicial(): SincronizacaoCrm {
  return {
    situacao: 'nao_sincronizada',
    cotacaoId: null,
    cotacaoNumero: null,
    correlacaoId: null,
    sincronizadoEm: null,
    erro: null,
  };
}

/**
 * Migra a versão 1 para a 2.
 *
 * A versão 2 acrescentou o vínculo com a oportunidade do CRM, o estado da sincronização e o bloco de
 * formalização (decisões D13 e D14).
 *
 * Os três campos entram como **nulos**, e isso é uma afirmação deliberada, não uma conveniência: um
 * documento da versão 1 nasceu sem oportunidade, e inventar um número aqui produziria vínculo falso
 * com o CRM. Nulo diz a verdade — esta proposta não é rastreável até uma oportunidade — que é a
 * mesma postura que a Task 16 adota para as premissas de cálculo perdidas do legado.
 */
function migrarV1ParaV2(doc: Record<string, unknown>): Record<string, unknown> {
  return {
    ...doc,
    versaoSchema: 2,
    oportunidade: null,
    sincronizacao: sincronizacaoInicial(),
    formalizacao: null,
  };
}

/** Passos de migração, indexados pela versão de origem. */
const MIGRACOES: Readonly<
  Record<number, (doc: Record<string, unknown>) => Record<string, unknown>>
> = {
  1: migrarV1ParaV2,
};

/**
 * Migra um documento de uma versão anterior de schema para a atual.
 *
 * Aplica os passos em cadeia: um documento na versão 1 passa por `migrarV1ParaV2` e segue adiante se
 * houver mais passos. O teste de migração garante que um documento antigo continua legível — sem
 * isso, evoluir o schema quebraria propostas já emitidas.
 */
export function migrarDocumento(bruto: unknown): DocumentoProposta {
  if (typeof bruto !== 'object' || bruto === null || Array.isArray(bruto)) {
    throw new ErroMigracao('O documento precisa ser um objeto.', typeof bruto);
  }

  let atual = bruto as Record<string, unknown>;
  const versaoOriginal = atual.versaoSchema;

  if (versaoOriginal === undefined) {
    throw new ErroMigracao(
      'O documento não declara `versaoSchema`. Não é possível migrar com segurança.',
      versaoOriginal,
    );
  }

  if (typeof versaoOriginal !== 'number' || !Number.isInteger(versaoOriginal)) {
    throw new ErroMigracao(
      `Versão de schema ${JSON.stringify(versaoOriginal)} não é reconhecida. Versão atual: ${String(VERSAO_SCHEMA_ATUAL)}.`,
      versaoOriginal,
    );
  }

  if (versaoOriginal > VERSAO_SCHEMA_ATUAL) {
    throw new ErroMigracao(
      `O documento está na versão ${String(versaoOriginal)}, mais nova que a suportada (${String(VERSAO_SCHEMA_ATUAL)}). Atualize a aplicação.`,
      versaoOriginal,
    );
  }

  // Aplica os passos em cadeia até chegar à versão atual.
  for (let v = versaoOriginal; v < VERSAO_SCHEMA_ATUAL; v += 1) {
    const passo = MIGRACOES[v];
    if (passo === undefined) {
      throw new ErroMigracao(
        `Não existe migração da versão ${String(v)} para a ${String(v + 1)}.`,
        versaoOriginal,
      );
    }
    atual = passo(atual);
  }

  const r = documentoProposta.safeParse(atual);
  if (!r.success) {
    const detalhe = formatarProblemas(r.error)
      .map((p) => `${p.campo}: ${p.mensagem}`)
      .join('; ');
    throw new ErroMigracao(
      `Documento migrado da versão ${String(versaoOriginal)} para a ${String(VERSAO_SCHEMA_ATUAL)} é inválido. ${detalhe}`,
      versaoOriginal,
    );
  }

  return r.data;
}
